import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import moment from 'moment';
import { translationKeys as calTranslationKeys } from '@gik/calendar/i18n/en';
import { useTranslation } from 'react-i18next';
import { dateTimeFormat, timeDisplayNoMeridiemFormat, timeMeridiemOnlyFormat } from '@gik/l10n';
import { convertDateFromBackend } from '@gik/core/utils/l10n';

export interface IDateTimeDisplayProps extends React.HTMLAttributes<HTMLElement> {
  startsAt?: string;
  endsAt?: string;
  allDay?: boolean;
  dateTimeSeparator?: string;
  timeSeparator?: string;
  shortTimes?: boolean;
  hideDate?: boolean;
  hideTime?: boolean;
}

export default function DateTimeDisplay({
  startsAt = moment().format(dateTimeFormat),
  endsAt = moment().format(dateTimeFormat),
  allDay = false,
  hideDate,
  hideTime,
  className,
  shortTimes,
  dateTimeSeparator = '•',
  timeSeparator = ' - ',
  ...otherProps
}: IDateTimeDisplayProps): React.ReactElement {
  const bem = bemBlock('date-time-display');
  const { t } = useTranslation();

  const startTime = convertDateFromBackend(startsAt);
  const endTime = convertDateFromBackend(endsAt);

  let startTimeDisplay = startTime.format(timeDisplayNoMeridiemFormat) + startTime.format(timeMeridiemOnlyFormat);

  if (shortTimes) {
    startTimeDisplay = startTimeDisplay.replace(':00', '');
  }

  let endTimeDisplay = endTime.format(timeDisplayNoMeridiemFormat) + endTime.format(timeMeridiemOnlyFormat);
  if (shortTimes) {
    endTimeDisplay = endTimeDisplay.replace(':00', '');
  }

  return (
    <div {...otherProps} className={bem(null, null, className)}>
      {!hideDate && <span className={bem('date')}>{startTime.format('MMMM D, YYYY')}</span>}
      {!hideDate && !hideTime && dateTimeSeparator !== null && (
        <span className={bem('separator')}>{dateTimeSeparator}</span>
      )}
      {!hideTime && (
        <>
          {!hideDate && <span className={bem('spacer')}>&nbsp;</span>}
          <span className={bem('time')}>
            {!allDay ? (
              <>
                <span className={bem('startTime')}>{startTimeDisplay}</span>
                <span className={bem('timeSeparator')}>{timeSeparator}</span>
                <span className={bem('endTime')}>{endTimeDisplay}</span>
              </>
            ) : (
              <span className={bem('allDay')}>{t(calTranslationKeys.allDay)}</span>
            )}
          </span>
        </>
      )}
    </div>
  );
}
